// Core Modules
import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// External Modules
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxPermissionsModule } from 'ngx-permissions';

// Services
import { AccessControlService } from '@core/services/access-control/access-control.service';
import { AuthService } from '@core/services/auth/auth.service';
import { ConfigService } from '@core/services/config/config.service';
import { SessionManagerService } from '@core/services/session-manager/session-manager.service';

// Components
import { MyAccessModalComponent } from './components/my-access-modal/my-access-modal.component';

// Models
import { HeaderContentModel } from '@shared/models/header-content.model';
import { IUserProfileBasedOnFields } from '@shared/models/auth0-types.model';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';
import { UserProfileFieldsEnum } from '@shared/enum/user-profile-fields.enum';

@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrl: './user-settings.component.scss',
  imports: [NgbDropdownModule, NgxPermissionsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsComponent implements OnInit {
  // Injectors
  private readonly accessControl = inject(AccessControlService);
  private readonly auth = inject(AuthService);
  private readonly configService = inject(ConfigService);
  private readonly modal = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly sessionManager = inject(SessionManagerService);

  // Private Members
  private userProfile?: IUserProfileBasedOnFields;

  // Public Members
  public headerContents: HeaderContentModel = this.configService.getContents('header');
  public userEmailName?: string;
  public userNameInitials?: string;
  // Right-aligned dropdown badge; null hides it until the cached /auth/me body is present.
  public readonly scopeCount = computed<number | null>(() => this.accessControl.userDetails()?.scopes.length ?? null);

  public ngOnInit(): void {
    this.userProfile = this.auth.getUserProfileBasedOnFields([UserProfileFieldsEnum.EMAIL, UserProfileFieldsEnum.HG_CUSTOMER]);
    this.userEmailName = this.userProfile?.email ? this.userProfile.email : 'NOT_AVAILABLE';
    this.getUserNameInitials();
  }

  public getUserNameInitials(): void {
    if (this.userEmailName && this.userEmailName !== 'NOT_AVAILABLE') {
      this.userNameInitials = this.userEmailName.charAt(0).toUpperCase();
    }
  }

  public logout(): void {
    this.sessionManager.stopWatching();
    this.auth.logout();
  }

  public navigateToMyTokens(): void {
    this.router.navigate([`/${MainNavigationEnum.TOKENS}`], { state: { email: this.userEmailName } });
  }

  public openMyAccess(): void {
    this.modal.open(MyAccessModalComponent, { size: 'lg', scrollable: true, centered: true, windowClass: 'my-access-modal' });
  }

  public openIssueTokenForMe(): void {
    this.router.navigate([`/${MainNavigationEnum.TOKENS}`], { state: { email: this.userEmailName, issue: true } });
  }
}
